import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import InterventionTab from '../InterventionTab';
import SimulationOutput from './SimulationOutput';
import { api } from '../../api/client';
import type { PricingMixResponse } from '../../types';
import Code from '../Code';

const SKUS: Array<{ id: string; label: string; family: 'arizona' | 'pure_leaf' | 'coke' | 'monster' }> = [
  { id: 'arizona_hero', label: 'AZ Hero Can (99¢)', family: 'arizona' },
  { id: 'arizona_bfy', label: 'AZ BFY Functional', family: 'arizona' },
  { id: 'pure_leaf_unsweet', label: 'Pure Leaf Unsweetened', family: 'pure_leaf' },
  { id: 'coke_zero_sugar', label: 'Coca-Cola Zero Sugar', family: 'coke' },
  { id: 'monster_zero_sugar', label: 'Monster Zero Sugar', family: 'monster' },
];

export default function PricingMixConsole() {
  const [changes, setChanges] = useState<Record<string, number>>({
    arizona_hero: 0,
    arizona_bfy: -0.05,
    pure_leaf_unsweet: 0,
    coke_zero_sugar: 0,
    monster_zero_sugar: 0,
  });
  const [handoffEnabled, setHandoffEnabled] = useState(true);

  const run = useMutation({
    mutationFn: () => api.pricingMixRun(changes, handoffEnabled),
  });

  return (
    <InterventionTab
      objective="O1 + O3 · price/promo elasticity without breaking the 99¢ floor"
      workflow={[
        'Set price-change percentage per SKU (own portfolio + competitor SKUs)',
        'Log-log demand model computes volume, revenue, margin response',
        'Cross-elasticities detect predicted competitor counter',
        'Hand off counter to War-Game best-response if magnitude > 10%',
      ]}
      decisionRules={{
        rules: [
          'BLOCK any scenario that reprices arizona_hero (N1 veto) · still shown in trade-off',
          'Flag if predicted volume shift > 15%',
          'Promote to War-Game if predicted competitor demand change > 10%',
        ],
        vetoes: ['N1 reprices_hero_can'],
      }}
      aiLogic={{
        description: 'Deterministic log-log elasticity model with own-price + cross-price terms. Calibrated from public category data; replaceable with Circana / NielsenIQ via MarketDataProvider (V6).',
        modality: 'deterministic',
        hitlLevel: 'L3 approval gating',
      }}
      kpi={{
        primary: 'Portfolio revenue + margin delta + BLOCKED moves',
        downstream: 'Feeds War-Game when competitor counter predicted',
        owner: 'Sales / Trade Mktg / Finance',
      }}
      content={
        <div className="panel">
          <div className="qhead">
            <span className="qdot" style={{ background: 'var(--gold)' }} />
            Price-change console · adjust per SKU
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            {SKUS.map((sku) => {
              const v = changes[sku.id] ?? 0;
              return (
                <div key={sku.id} style={{ padding: '10px 12px', background: 'var(--panel2)', border: '1px solid var(--line)', borderRadius: 6 }}>
                  <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{sku.label}</span>
                    <span className="mono" style={{ fontSize: 13, color: v < 0 ? 'var(--red)' : v > 0 ? 'var(--olive)' : 'var(--muted)', fontWeight: 600 }}>
                      {v > 0 ? '+' : ''}{(v * 100).toFixed(1)}%
                    </span>
                  </div>
                  <input
                    className="slider"
                    type="range"
                    min={-0.5}
                    max={0.5}
                    step={0.01}
                    value={v}
                    onChange={(e) => setChanges((s) => ({ ...s, [sku.id]: parseFloat(e.target.value) }))}
                  />
                </div>
              );
            })}
          </div>

          <div className="row" style={{ gap: 12, marginBottom: 14 }}>
            <label className="small" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={handoffEnabled} onChange={(e) => setHandoffEnabled(e.target.checked)} />
              Hand competitor counter off to War-Game
            </label>
            <button type="button" className="btn primary" onClick={() => run.mutate()} disabled={run.isPending}>
              {run.isPending ? 'Simulating…' : '⚡ Run Pricing & Mix'}
            </button>
          </div>

          {run.data && <PricingMixResult data={run.data} changes={changes} />}
        </div>
      }
    />
  );
}

function PricingMixResult({ data, changes }: { data: PricingMixResponse; changes: Record<string, number> }) {
  const portfolio = data.portfolio_delta;
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 14 }}>
        <Stat label="Portfolio revenue Δ" value={dollar(portfolio.revenue_delta)} color={portfolio.revenue_delta >= 0 ? 'olive' : 'red'} />
        <Stat label="Portfolio margin Δ" value={dollar(portfolio.margin_delta)} color={portfolio.margin_delta >= 0 ? 'olive' : 'red'} />
        <Stat label="Competitor counter?" value={data.competitor_response_predicted ? 'Predicted' : 'None'} color={data.competitor_response_predicted ? 'gold' : 'muted'} />
      </div>

      <div className="panel" style={{ background: 'var(--panel2)', marginBottom: 14 }}>
        <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
          Per-SKU outcome
        </div>
        <table className="c2-table" style={{ width: '100%', fontSize: 13 }}>
          <thead>
            <tr>
              <th align="left">SKU</th>
              <th align="right">Δ price</th>
              <th align="right">Predicted volume</th>
              <th align="right">Δ volume</th>
              <th align="right">Predicted margin</th>
            </tr>
          </thead>
          <tbody>
            {data.predicted_outcomes.map((o) => (
              <tr key={o.sku}>
                <td><span className="mono">{o.sku}</span></td>
                <td align="right" className="mono">{(o.price_change_pct * 100).toFixed(1)}%</td>
                <td align="right" className="mono">{o.predicted_volume.toFixed(1)}M</td>
                <td align="right" className="mono" style={{ color: o.volume_delta_pct >= 0 ? 'var(--olive)' : 'var(--red)' }}>
                  {o.volume_delta_pct > 0 ? '+' : ''}{(o.volume_delta_pct * 100).toFixed(2)}%
                </td>
                <td align="right" className="mono">${o.predicted_margin.toFixed(2)}M</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.blocked_moves.length > 0 && (
        <div className="panel" style={{ background: 'rgba(207,84,64,0.08)', border: '1px solid var(--red)', marginBottom: 14 }}>
          <div className="small" style={{ color: 'var(--red)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 6 }}>
            BLOCKED moves · visible, not hidden
          </div>
          {data.blocked_moves.map((b) => (
            <div key={b.sku + b.veto_id} style={{ marginBottom: 4, fontSize: 13 }}>
              <span className="tag red" style={{ marginRight: 8 }}><Code id={b.veto_id} kind="non_objective" compact /></span>
              <span className="mono">{b.sku}</span> attempted {(b.attempted_change_pct * 100).toFixed(1)}% — {b.reason}
            </div>
          ))}
        </div>
      )}

      {data.competitor_response_predicted && (
        <div className="panel" style={{ background: 'var(--panel2)', marginBottom: 14, borderLeft: '3px solid var(--gold)' }}>
          <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            Competitor counter predicted · War-Game handoff
          </div>
          <div style={{ fontSize: 14, marginBottom: 6 }}>
            <b className="mono">{data.competitor_response_predicted.twin_id}</b> ({data.competitor_response_predicted.sku}) likely plays{' '}
            <b style={{ color: 'var(--gold)' }}>{data.competitor_response_predicted.counter_type}</b> · magnitude{' '}
            <span className="mono">{(data.competitor_response_predicted.shock.magnitude * 100).toFixed(1)}%</span>
          </div>
          {data.wargame_handoff_id && (
            <div className="small mono faint">handoff id: {data.wargame_handoff_id}</div>
          )}
        </div>
      )}

      {/* 4-part Move Brief */}
      <div style={{ marginTop: 14 }}>
        <PricingMixMoveBrief data={data} changes={changes} />
      </div>
    </>
  );
}

function PricingMixMoveBrief({ data, changes }: { data: PricingMixResponse; changes: Record<string, number> }) {
  const heroBlocked = data.blocked_moves.some((b) => b.sku === 'arizona_hero');
  const competitorCounter = data.competitor_response_predicted;
  const portfolioMarginPositive = data.portfolio_delta.margin_delta >= 0;
  const decideBy = new Date(Date.now() + 21 * 86400 * 1000).toISOString().slice(0, 10);

  const recommendation = heroBlocked
    ? `Hold the hero-can 99¢ floor; execute the BFY price ladder (${changes.arizona_bfy ? (changes.arizona_bfy * 100).toFixed(0) : 0}%) within the operating window.`
    : portfolioMarginPositive
      ? 'Execute the proposed price-pack ladder; lock the move within 21 days before the next retailer reset.'
      : 'Hold the price book; the proposed ladder loses portfolio margin without offsetting volume.';

  const recommendationDetail = portfolioMarginPositive
    ? `Portfolio margin Δ ${dollar(data.portfolio_delta.margin_delta)} · revenue Δ ${dollar(data.portfolio_delta.revenue_delta)}. ${competitorCounter ? `Twin ${competitorCounter.twin_id} likely counters with ${competitorCounter.counter_type} — handoff sent to War-Game.` : 'No competitor counter predicted at the current magnitudes.'}`
    : `Predicted portfolio margin Δ ${dollar(data.portfolio_delta.margin_delta)}. Roll back; reconsider with smaller per-SKU moves or pair with a hero-protection program.`;

  return (
    <SimulationOutput
      onDispose={async (d, _driftId, decision) => {
        await api.simulationDisposition({
          simulation_kind: 'pricing_mix',
          signal_id: 'S-03',  // competitor pricing pressure is the canonical trigger
          disposition: d,
          owner: 'Sales / Trade Mktg / Finance',
          recommendation,
          predicted_outcome: {
            margin_delta: data.portfolio_delta.margin_delta,
            revenue_delta: data.portfolio_delta.revenue_delta,
            blocked_count: data.blocked_moves.length,
            competitor_counter: competitorCounter?.counter_type ?? null,
          },
          decision,
        });
      }}
      recommendation={recommendation}
      recommendationDetail={recommendationDetail}
      evidence={{
        headline: portfolioMarginPositive
          ? 'Log-log demand model: BFY ladder lifts mix without triggering veto. Cross-elasticities show competitor counter risk if hero-can is touched.'
          : 'Log-log demand model says the proposed move loses more margin than the volume gain recovers.',
        metrics: [
          { label: 'Portfolio revenue Δ', value: dollar(data.portfolio_delta.revenue_delta), color: data.portfolio_delta.revenue_delta >= 0 ? 'olive' : 'red' },
          { label: 'Portfolio margin Δ', value: dollar(data.portfolio_delta.margin_delta), color: portfolioMarginPositive ? 'olive' : 'red' },
          { label: 'Hero protected', value: heroBlocked ? 'BLOCKED ok' : 'ok', color: 'olive' },
          { label: 'Counter risk', value: competitorCounter ? competitorCounter.counter_type : 'none', color: competitorCounter ? 'gold' : 'olive' },
        ],
        twinCounter: competitorCounter ? `${competitorCounter.twin_id} predicted to play ${competitorCounter.counter_type} (magnitude ${(competitorCounter.shock.magnitude * 100).toFixed(1)}%)` : undefined,
        exposedAssumption: 'Bets on category elasticities holding within the current data window; recalibrate quarterly via MarketDataProvider when V6 lands.',
      }}
      alternatives={[
        {
          rank: 2,
          move: 'Hold the entire price book · do nothing',
          expected: 'Portfolio margin Δ $0 · cedes BFY mix migration to KDP',
          whyLower: 'No defense against shelf-share erosion in functional segment',
        },
        {
          rank: 3,
          move: 'Pull all promo, redirect to earned-media collab',
          expected: 'Portfolio revenue Δ ~−$3M (short term) · longer-term LTV upside unmodelled here',
          whyLower: 'Pricing-Mix simulator doesn\'t price the LTV side; needs Brand engine confirmation',
        },
      ]}
      blocked={data.blocked_moves.map((b) => ({
        move: `Reprice ${b.sku} by ${(b.attempted_change_pct * 100).toFixed(1)}%`,
        vetoId: b.veto_id,
        vetoReason: b.reason,
      }))}
      actionPath={{
        owner: 'Sales / Trade Mktg · Finance signs off on margin Δ',
        decideBy,
        tripwire: 'Hero-can velocity falls > 6% week-over-week OR predicted competitor counter materializes within 21 days',
        rollback: 'Roll back price-pack ladder to prior shelf prices within 7 days of trigger',
      }}
    />
  );
}

function Stat({ label, value, color }: { label: string; value: string; color: 'olive' | 'red' | 'gold' | 'muted' }) {
  const c = { olive: 'var(--olive)', red: 'var(--red)', gold: 'var(--gold)', muted: 'var(--muted)' }[color];
  return (
    <div className="panel" style={{ padding: 14, background: 'var(--panel2)' }}>
      <div className="small faint" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{label}</div>
      <div className="num" style={{ color: c, fontSize: 22 }}>{value}</div>
    </div>
  );
}

function dollar(v: number): string {
  const abs = Math.abs(v);
  const sign = v < 0 ? '−' : '+';
  return `${sign}$${abs.toFixed(2)}M`;
}
